import { useMemo } from 'react'

import { useCurrentOrgId } from '~/store/slices/currentOrg'

import type { SynopLinkModel } from '../../client/ders'
import useManagedOrgTree from './organizations'
import useDataManager from './useDataManager'
import { EMPTY, useNormalizedFilters } from './utils'

type SynopLinkFilters = {
  orgId?: string
  siteId?: string
}

type UseManagedSynopLinksInput = {
  filters?: SynopLinkFilters
}

export default function useManagedSynopLinks({ filters }: UseManagedSynopLinksInput = {}) {
  const { synoplinks } = useDataManager()
  const { data, ...rest } = synoplinks

  return {
    ...rest,
    synoplinks: useFilteredSynopLinks(data, filters)
  }
}

function useFilteredSynopLinks(data: SynopLinkModel[], filters?: SynopLinkFilters) {
  const currentOrgId = useCurrentOrgId()
  const { orgTree } = useManagedOrgTree()

  const normalizedFilters = useNormalizedFilters(filters, {
    orgId: currentOrgId || undefined
  })

  return useMemo(() => {
    const { orgId, siteId } = normalizedFilters

    if (siteId === EMPTY) {
      return []
    }

    let filtered = data

    if (orgId != null) {
      const descendants = orgTree.indexes.descendantIds.get(orgId)
      filtered = data.filter(
        (synoplink) => synoplink.organizationId === orgId || descendants?.has(synoplink.organizationId)
      )
    }

    if (siteId != null) {
      filtered = filtered.filter((synoplink) => synoplink.siteId === siteId)
    }

    return filtered
  }, [data, normalizedFilters, orgTree])
}
